import SimulatorInformation, { simulatorData } from "./elements/SimulatorInformation";
import { DocumentTextIcon, PencilIcon } from "@heroicons/react/24/outline";
import { EDistributionFrequency } from "common/enums/ProductSheet/EDistributionFrequency";
import { EProductFileType } from "common/enums/ProductSheet/EProductFileType";
import { EProductCategories } from "common/enums/Scpi/EProductCategories";
import ProductSheetResponseResource from "common/resources/ProductSheet/ProductSheetResponseResource";
import ProductSheetUpdateRequestResource from "common/resources/ProductSheet/ProductSheetUpdateRequestResource";
import { ValidationError } from "common/resources/Resource";
import Button, { EButtonVariant } from "components/elements/Button";
import NavTabComponent from "components/elements/NavTabComponent";
import Typography, { ITypo } from "components/elements/Typography";
import Form from "components/materials/Form";
import HasRules, { useHasRules } from "components/materials/HasRules";
import I18n from "components/materials/I18n";
import PageTemplate from "components/PageTemplate";
import ModuleConfig from "configs/ModuleConfig";
import useOpenable from "hooks/useOpenable";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ProductService from "services/ProductService";
import { container } from "tsyringe";
import GlobalUtils from "utils/GlobalUtils";

import classes from "./classes.module.scss";
import Document from "./elements/Document";
import DocumentsTable from "./elements/DocumentsTable";
import DemembrementKeysTable, { IDemembrementKeyEdit } from "./elements/DemembrementKeysTable";
import ProductInformation, { EProductInformation, IDueDate } from "./elements/ProductInformation";
import HistoryModal from "./modals/HistoryModal";
import { ETechnicalSignature } from "common/enums/ProductSheet/ETechnicalSignature";
import { EProductType } from "common/enums/ProductSheet/EProductType";

const productService = container.resolve(ProductService);
const modulePages = container.resolve(ModuleConfig).get().modules.pages;
const productModule = modulePages.Products;

export default function Product() {
	const hasMiddleOfficeAccess = useHasRules({ AND: { access_platform__middle_office: true } });
	const [errors, setErrors] = useState<ValidationError[]>([]);
	const [product, setProduct] = useState<ProductSheetResponseResource | null>(null);
	const [productLoadError, setProductLoadError] = useState(false);
	const [productSheets, setProductSheets] = useState<ProductSheetResponseResource[]>([]);
	const [latestProductSheet, setLatestProductSheet] = useState<ProductSheetResponseResource | null>(null);
	const { productId } = useParams();
	const [readonly, setReadonly] = useState(true);
	const navigate = useNavigate();
	const { isOpen, open, close } = useOpenable();

	const [dueDate, setDueDate] = useState<IDueDate | null>(null);
	const [demembrementKeys, setDemembrementKeys] = useState<IDemembrementKeyEdit[]>([]);

	const [simulationData, setSimulationData] = useState(() => simulatorData(null));

	const [historySelected, setHistorySelected] = useState<EProductInformation | null>(null);
	const onCancelEdit = useCallback(() => {
		setReadonly(true);
		setSimulationData(simulatorData(product?.simulatorData));
		setDueDate(product && product.dueDays && product.dueMonths ? { dueDays: product.dueDays, dueMonths: product.dueMonths } : null);
		setDemembrementKeys(product?.demembrementKeys?.map(({ duration, usufruitRatio, nueProprieteRatio }) => ({ duration, usufruitRatio, nueProprieteRatio })) ?? []);
	}, [product]);

	const fetchProductAndProductSheets = useCallback(() => {
		if (!productId) return;
		setProductLoadError(false);
		return productService
			.getAllProductSheetsByProductId(productId)
			.then((products) => {
				if (products.length === 0) {
					setProductLoadError(true);
					return;
				}
				setProduct(products[0]!);
				setProductSheets(products);
			})
			.catch(() => setProductLoadError(true));
	}, [productId]);

	useEffect(() => {
		if (!productId) return;
		productService.getLastProductSheetByProductId(productId).then(setLatestProductSheet).catch(console.error);
	}, [productId]);

	useEffect(() => {
		fetchProductAndProductSheets();
	}, [fetchProductAndProductSheets]);

	useEffect(() => {
		setSimulationData(simulatorData(product?.simulatorData));
		setDemembrementKeys(product?.demembrementKeys?.map(({ duration, usufruitRatio, nueProprieteRatio }) => ({ duration, usufruitRatio, nueProprieteRatio })) ?? []);
	}, [product]);

	const onSubmit = useCallback(
		(e: React.FormEvent<HTMLFormElement>, formData: { [key: string]: unknown }) => {
			e.preventDefault();
			setErrors([]);
			if (!product?.productId) return;
			ProductSheetUpdateRequestResource.hydrate<ProductSheetUpdateRequestResource>({
				description: product.description,
				simulatorData: simulationData,
				name: (formData["name"] as string) || product.name,
				price: Number(formData["price"]),
				dueDays: "dueDays" in formData ? Number(formData["dueDays"]) : product.dueDays ?? 0,
				dueMonths: "dueMonths" in formData ? Number(formData["dueMonths"]) : product.dueMonths ?? 0,
				ifiDate: GlobalUtils.isSet(formData["ifiDate"]) ? new Date(formData["ifiDate"] as Date) : null,
				ifiValue: GlobalUtils.isSet(formData["ifiValue"]) ? Number(formData["ifiValue"]) : null,
				minNumberOfShares: GlobalUtils.isSet(formData["minNumberOfShares"]) ? Number(formData["minNumberOfShares"]) : null,
				distributionRate: !("distributionRate" in formData)
					? product.distributionRate ?? null
					: GlobalUtils.isSet(formData["distributionRate"])
						? Number(formData["distributionRate"])
						: null,
				distributionRateNet: !("distributionRateNet" in formData)
					? product.distributionRateNet ?? null
					: GlobalUtils.isSet(formData["distributionRateNet"])
						? Number(formData["distributionRateNet"])
						: null,
				distributionRateN2: GlobalUtils.isSet(formData["distributionRateN2"]) ? Number(formData["distributionRateN2"]) : null,
				distributionRateDate: !("distributionRateDate" in formData)
					? product.distributionRateDate ?? null
					: GlobalUtils.isSet(formData["distributionRateDate"])
						? new Date(formData["distributionRateDate"] as Date)
						: null,
				distributionRateN2Date: GlobalUtils.isSet(formData["distributionRateN2Date"]) ? new Date(formData["distributionRateN2Date"] as Date) : null,
				distributionFrequency: !("distributionFrequency" in formData)
					? product.distributionFrequency ?? null
					: formData["distributionFrequency"]
						? (formData["distributionFrequency"] as EDistributionFrequency)
						: null,
				capitalization: !("capitalization" in formData)
					? product.capitalization ?? null
					: GlobalUtils.isSet(formData["capitalization"])
						? Number(formData["capitalization"])
						: null,
				financialOccupancyRate: GlobalUtils.isSet(formData["financialOccupancyRate"]) ? Number(formData["financialOccupancyRate"]) : null,
				retainedEarnings: GlobalUtils.isSet(formData["retainedEarnings"]) ? Number(formData["retainedEarnings"]) : null,
				subFees: !("subFees" in formData) ? product.subFees ?? null : GlobalUtils.isSet(formData["subFees"]) ? Number(formData["subFees"]) : null,
				withdrawalPrice: !("withdrawalPrice" in formData)
					? product.withdrawalPrice ?? null
					: GlobalUtils.isSet(formData["withdrawalPrice"])
						? Number(formData["withdrawalPrice"])
						: null,
				category: formData["category"] as EProductCategories,
				productId: product.productId,
				demembrementKeys: demembrementKeys.length ? demembrementKeys : undefined,
				avantageFiscal: GlobalUtils.isSet(formData["avantageFiscal"]) ? (formData["avantageFiscal"] as string) : undefined,
				droitsEntree: GlobalUtils.isSet(formData["droitsEntree"]) ? (formData["droitsEntree"] as string) : undefined,
				eligiblePea: formData["eligiblePea"] === undefined ? undefined : formData["eligiblePea"] === "true",
				eligible150_0BTer: formData["eligible150_0BTer"] === undefined ? undefined : formData["eligible150_0BTer"] === "true",
			})
				.validateOrReject()
				.then((resource) => productService.update(resource))
				.then(() => fetchProductAndProductSheets())
				.then(() => setReadonly(true))
				.catch((errors) => {
					console.warn(errors);
					if (errors instanceof Array) setErrors(errors);
				});
		},
		[product, demembrementKeys, simulationData, fetchProductAndProductSheets],
	);

	const openHistory = useCallback(
		(productInformation: EProductInformation) => {
			setHistorySelected(productInformation);
			open();
		},
		[open],
	);

	const redirectToFiles = useCallback(() => {
		if (!product) return;
		if (product.isSignable === null) return;
		navigate(productModule.props.pages.Product.props.pages.DocumentTemplateSubscription.props.pages.PhysicalPerson.props.path.replace(":productId", product.productId));
	}, [navigate, product]);

	const toggleElectronicSignature = useCallback(() => {
		if (!latestProductSheet) return;
		productService
			.toggleElectronicSignature(latestProductSheet.id)
			.then(() => fetchProductAndProductSheets())
			.catch(console.error);
	}, [latestProductSheet, fetchProductAndProductSheets]);

	if (!product)
		return (
			<PageTemplate
				tabTitle={I18n.trslt(I18n.asset.pages.product.page_title)}
				backArrow={{ overridePreviousClick: { onClick: () => navigate(productModule.props.path), text: I18n.asset.pages.product.back_to_products }, enabled: true }}>
				{productLoadError ? (
					<div role="alert">
						<p>Impossible de charger la fiche produit.</p>
						<Button onClick={() => fetchProductAndProductSheets()}>Réessayer</Button>
					</div>
				) : (
					<p role="status">Chargement de la fiche produit…</p>
				)}
			</PageTemplate>
		);

	const isScpi = product.product?.productType === EProductType.scpi;
	const isPrivateEquity = product.product?.productType === EProductType.private_equity;

	return (
		<PageTemplate
			tabTitle={I18n.trslt(I18n.asset.pages.product.page_title)}
			backArrow={{ overridePreviousClick: { onClick: () => navigate(productModule.props.path), text: I18n.asset.pages.product.back_to_products }, enabled: true }}>
			<div className={classes["root"]}>
				<div className={classes["header"]}>
					<div className={classes["header-title"]}>
						<Typography typo={ITypo.H1}>{product.name}</Typography>
						<Typography typo={ITypo.P_LARGE}>
							{I18n.asset.pages.product.own_by}: <strong>{product.societeDeGestion?.name}</strong>
						</Typography>
					</div>
					<HasRules requiredRules={{ AND: { access_platform__middle_office: true } }}>
						<div className={classes["button-container"]}>
							<Button iconposition="right" icon={<DocumentTextIcon />} disabled={product.isSignable === null} onClick={toggleElectronicSignature}>
								{product.technicalSignatureType === ETechnicalSignature.manual
									? I18n.asset.pages.product.enable_electronic_signature
									: I18n.asset.pages.product.disable_electronic_signature}
							</Button>
							<Button iconposition="right" icon={<DocumentTextIcon />} disabled={product.isSignable === null} onClick={redirectToFiles}>
								{I18n.asset.pages.product.subscription_documents}
							</Button>
						</div>
					</HasRules>
				</div>

				<Typography typo={ITypo.H2} className={classes["informations-title"]}>
					{I18n.trslt(I18n.asset.pages.product.informations.title)}
				</Typography>

				<Form onSubmit={onSubmit} errors={errors} className={classes["informations-container"]}>
					<div className={classes["product-information"]}>
						<ProductInformation productInformation={EProductInformation.name} defaultValue={product.name} readonly={readonly} openHistory={openHistory} />
						<ProductInformation productInformation={EProductInformation.price} defaultValue={product.price} readonly={readonly} openHistory={openHistory} />
						<ProductInformation productInformation={EProductInformation.category} defaultValue={product.category} readonly={readonly} openHistory={openHistory} />
						<ProductInformation
							productInformation={EProductInformation.minNumberOfShares}
							defaultValue={product.minNumberOfShares}
							readonly={readonly}
							openHistory={openHistory}
						/>
						{/* Frais, jouissance, retrait, distribution, capitalisation : sans objet en private equity. */}
						{!isPrivateEquity && (
							<>
								<ProductInformation productInformation={EProductInformation.subFees} defaultValue={product.subFees} readonly={readonly} openHistory={openHistory} />
								<ProductInformation
									productInformation={EProductInformation.dueDate}
									dueDate={{ value: { dueDays: dueDate?.dueDays || product.dueDays, dueMonths: dueDate?.dueMonths || product.dueMonths }, onChange: setDueDate }}
									readonly={readonly}
									openHistory={openHistory}
								/>
								<ProductInformation
									productInformation={EProductInformation.withdrawalPrice}
									defaultValue={product.withdrawalPrice}
									readonly={readonly}
									openHistory={openHistory}
								/>
								<ProductInformation
									productInformation={EProductInformation.distributionRate}
									distributionRate={{
										value: product.distributionRate,
										date: product.distributionRateDate,
									}}
									i18nVars={{ year: product.distributionRateDate ? new Date(product.distributionRateDate).getFullYear().toString() : "N-1" }}
									readonly={readonly}
									openHistory={openHistory}
								/>
								{isScpi && (
									<ProductInformation
										productInformation={EProductInformation.distributionRateNet}
										defaultValue={product.distributionRateNet}
										readonly={readonly}
										openHistory={openHistory}
									/>
								)}
								{isScpi && (
									<ProductInformation
										productInformation={EProductInformation.distributionRateN2}
										distributionRate={{
											value: product.distributionRateN2,
											date: product.distributionRateN2Date,
										}}
										i18nVars={{ year: product.distributionRateN2Date ? new Date(product.distributionRateN2Date).getFullYear().toString() : "N-2" }}
										readonly={readonly}
										openHistory={openHistory}
									/>
								)}
								<ProductInformation
									productInformation={EProductInformation.distributionFrequency}
									defaultValue={product.distributionFrequency}
									readonly={readonly}
									openHistory={openHistory}
								/>
								<ProductInformation
									productInformation={EProductInformation.capitalization}
									defaultValue={product.capitalization}
									readonly={readonly}
									openHistory={openHistory}
								/>
							</>
						)}
						{isScpi && (
							<>
								<ProductInformation
									productInformation={EProductInformation.financialOccupancyRate}
									defaultValue={product.financialOccupancyRate}
									readonly={readonly}
									openHistory={openHistory}
								/>
								<ProductInformation
									productInformation={EProductInformation.retainedEarnings}
									defaultValue={product.retainedEarnings}
									readonly={readonly}
									openHistory={openHistory}
								/>
								<ProductInformation
									productInformation={EProductInformation.ifiValue}
									ifiValue={{
										date: product.ifiDate,
										value: product.ifiValue,
									}}
									readonly={readonly}
									openHistory={openHistory}
								/>
							</>
						)}
						{!isScpi && (
							<>
								<ProductInformation
									productInformation={EProductInformation.avantageFiscal}
									defaultValue={product.fiscalInfo?.avantageFiscal ?? null}
									readonly={readonly}
									openHistory={openHistory}
								/>
								<ProductInformation
									productInformation={EProductInformation.droitsEntree}
									defaultValue={product.fiscalInfo?.droitsEntree ?? null}
									readonly={readonly}
									openHistory={openHistory}
								/>
								<ProductInformation
									productInformation={EProductInformation.eligiblePea}
									defaultValue={
										product.fiscalInfo?.eligiblePea === null || product.fiscalInfo?.eligiblePea === undefined
											? null
											: product.fiscalInfo.eligiblePea
												? "true"
												: "false"
									}
									readonly={readonly}
									openHistory={openHistory}
								/>
								<ProductInformation
									productInformation={EProductInformation.eligible150_0BTer}
									defaultValue={
										product.fiscalInfo?.eligible150_0BTer === null || product.fiscalInfo?.eligible150_0BTer === undefined
											? null
											: product.fiscalInfo.eligible150_0BTer
												? "true"
												: "false"
									}
									readonly={readonly}
									openHistory={openHistory}
								/>
							</>
						)}
					</div>
					{isScpi && (modulePages.Simulator.enabled || hasMiddleOfficeAccess) && (
						<SimulatorInformation value={simulationData} readonly={readonly} onChange={setSimulationData} />
					)}
					{demembrementKeys.length > 0 && <DemembrementKeysTable key={readonly.toString()} keys={demembrementKeys} readonly={readonly} onChange={setDemembrementKeys} />}
					{renderButtons()}
				</Form>

				{product.ProductToFiles && (
					<>
						<Typography typo={ITypo.H2} className={classes["informations-title"]}>
							{I18n.trslt(I18n.asset.pages.product.product_documentation_title)}
						</Typography>
						<NavTabComponent
							items={Object.values(EProductFileType).map((fileType) => {
								return {
									label: I18n.asset.enums.EProductFileType[fileType],
									children: getDocument(product, fileType),
								};
							})}
						/>
					</>
				)}
			</div>
			{historySelected && <HistoryModal isOpen={isOpen} onClose={close} productInformation={historySelected} productSheets={productSheets} />}
		</PageTemplate>
	);

	function getDocument(product: ProductSheetResponseResource, type: EProductFileType) {
		const productFile =
			product.ProductToFiles?.sort((a, b) => {
				return a.createdAt && b.createdAt && a.createdAt < b.createdAt ? 1 : -1;
			}).find((productFile) => productFile.type === type) || null;
		const trimestrialReports = product.ProductToFiles?.filter((productFile) => productFile.type === EProductFileType.trimestrial_report) || [];
		trimestrialReports.sort((a, b) => {
			if (b.createdAt && a.createdAt && a.createdAt < b.createdAt) return 1;
			if (b.createdAt && a.createdAt && a.createdAt > b.createdAt) return -1;
			return 0;
		});
		if (type === EProductFileType.trimestrial_report)
			return (
				<DocumentsTable
					productFiles={trimestrialReports}
					type={EProductFileType.trimestrial_report}
					productId={product.productId}
					onSuccess={(product) => setProduct(product)}
				/>
			);
		return <Document productFile={productFile} productId={product.productId} type={type} onUploadSuccess={setProduct} />;
	}

	function renderButtons() {
		if (readonly)
			return (
				<HasRules requiredRules={{ AND: { access_platform__middle_office: true } }}>
					<Button className={classes["button"]} icon={<PencilIcon />} iconposition="right" onClick={() => setReadonly(false)}>
						{I18n.asset.pages.client.sub_pages.personal_information.modify_information}
					</Button>
				</HasRules>
			);
		return (
			<HasRules requiredRules={{ AND: { access_platform__middle_office: true } }}>
				<div className={classes["button"]}>
					<Button type="submit">{I18n.asset.common.save}</Button>

					<Button onClick={onCancelEdit} variant={EButtonVariant.OUTLINED}>
						{I18n.asset.common.cancel}
					</Button>
				</div>
			</HasRules>
		);
	}
}
